import { useCallback, useEffect, useRef, RefObject } from "react";

interface TouchGestureConfig {
  minSwipeDistance?: number;
  maxVerticalDistance?: number;
  touchTimeThreshold?: number;
}

export const useTouchGestures = (
  containerRef: RefObject<HTMLElement>,
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  config: TouchGestureConfig = {}
) => {
  const {
    minSwipeDistance = 50,
    maxVerticalDistance = 100,
    touchTimeThreshold = 500,
  } = config;

  const touchStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);

  const onTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  const onTouchEnd = useCallback((e: TouchEvent) => {
    const touchStart = touchStartRef.current;
    if (!touchStart) return;

    const touch = e.changedTouches[0];
    const touchEnd = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    const deltaTime = touchEnd.time - touchStart.time;

    // Check if it's a valid swipe gesture
    const isHorizontalSwipe = Math.abs(deltaX) >= minSwipeDistance;
    const isWithinVerticalThreshold = deltaY <= maxVerticalDistance;
    const isWithinTimeThreshold = deltaTime <= touchTimeThreshold;

    if (isHorizontalSwipe && isWithinVerticalThreshold && isWithinTimeThreshold) {
      if (deltaX > 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }

    touchStartRef.current = null;
  }, [onSwipeLeft, onSwipeRight, minSwipeDistance, maxVerticalDistance, touchTimeThreshold]);

  const onTouchCancel = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const options = { passive: true };

    container.addEventListener('touchstart', onTouchStart, options);
    container.addEventListener('touchend', onTouchEnd, options);
    container.addEventListener('touchcancel', onTouchCancel, options);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [containerRef, onTouchStart, onTouchEnd, onTouchCancel]);
};